import { hasUnsavedFormChanges } from "../../common/formStateDirty";

type MemberFormDirtyState = {
  membership: {
    selectedAdditionalCostIds: string[];
  };
  companyDetails: {
    howDidYouHearAboutUs: string[];
    primaryReasonsForJoining: string[];
    activeSocialPlatforms: string[];
    // Optional: a Create Member draft saved before this field still loads.
    memberOwnershipDiversity?: string[];
  };
  insights: {
    communicationPreferences: string[];
    governanceGroupIds?: string[];
  };
  directory: {
    directoryCategoryIds: string[];
  };
};

type MemberFormDirtyComparisonInput<Value extends MemberFormDirtyState> = {
  baseline: Value;
  current: Value;
};

const canonicalizeSetValues = <Value extends string>(values: Value[]) =>
  Array.from(new Set(values)).sort((firstValue, secondValue) =>
    firstValue.localeCompare(secondValue)
  );

export const haveSameMemberSelectionValues = (
  firstValues: string[],
  secondValues: string[],
): boolean =>
  !hasUnsavedFormChanges({
    baseline: canonicalizeSetValues(firstValues),
    current: canonicalizeSetValues(secondValues),
  });

export const shouldRebaseUntouchedMemberSystemValue = <Value>({
  baseline,
  current,
  next,
}: {
  baseline: Value;
  current: Value;
  next: Value;
}): boolean =>
  !hasUnsavedFormChanges({ baseline, current }) &&
  hasUnsavedFormChanges({ baseline, current: next });

/**
 * Selection controls represent sets even when the UI or backend returns them
 * in a different order. Only those known set-like arrays are canonicalized.
 * Ordered referral, affinity-enrollment, and custom-field rows remain in the
 * full snapshot so a real row edit or reorder is still protected.
 */
export const getMemberFormDirtySnapshot = <
  Value extends MemberFormDirtyState,
>(value: Value): Value => ({
  ...value,
  membership: {
    ...value.membership,
    selectedAdditionalCostIds: canonicalizeSetValues(
      value.membership.selectedAdditionalCostIds
    ),
  },
  companyDetails: {
    ...value.companyDetails,
    howDidYouHearAboutUs: canonicalizeSetValues(
      value.companyDetails.howDidYouHearAboutUs
    ),
    primaryReasonsForJoining: canonicalizeSetValues(
      value.companyDetails.primaryReasonsForJoining
    ),
    activeSocialPlatforms: canonicalizeSetValues(
      value.companyDetails.activeSocialPlatforms
    ),
    // A missing list (older draft) is the same as no box ticked.
    memberOwnershipDiversity: canonicalizeSetValues(
      value.companyDetails.memberOwnershipDiversity ?? []
    ),
  },
  insights: {
    ...value.insights,
    ...(value.insights.governanceGroupIds === undefined ? {} : {
      governanceGroupIds: canonicalizeSetValues(value.insights.governanceGroupIds),
    }),
    communicationPreferences: canonicalizeSetValues(
      value.insights.communicationPreferences
    ),
  },
  directory: {
    ...value.directory,
    directoryCategoryIds: canonicalizeSetValues(
      value.directory.directoryCategoryIds
    ),
  },
});

export const hasUnsavedMemberFormChanges = <
  Value extends MemberFormDirtyState,
>({
  baseline,
  current,
}: MemberFormDirtyComparisonInput<Value>): boolean =>
  hasUnsavedFormChanges({
    baseline: getMemberFormDirtySnapshot(baseline),
    current: getMemberFormDirtySnapshot(current),
  });
